import * as Redux from '@reduxjs/toolkit';
import { push } from 'react-router-redux';
import type { IAppState, IAsynchronousAction, ILoader } from '../appstate';
import type { IBlueprint, IEnabledLocation } from '../domain';
import type { IToastMessage } from '../helpers/Toastr';
import { toastr } from '../helpers/Toastr';
import * as Hub from '../ogm-hub';

export interface ILocationAssignmentState {
	locations: ILoader<IEnabledLocation[]>;
	blueprint: ILoader<IBlueprint>;
	saveEnabledLocationsState: IAsynchronousAction;
	addedLocations: IEnabledLocation[];
	removedLocations: IEnabledLocation[];
	confirmationModalOpen: boolean;
	isFilterModalOpen: boolean;
	locationStates: string[];
	locationCityNames: string[];
	locationDcNames: string[];
	locationDcNumbers: string[];
	stateFilterValue: { label: string; value: string };
	assignmentFilterValue: { label: string; value: boolean };
	dcNameFilterValue: { label: string; value: string };
	dcNumberFilterValue: { label: string; value: string };
	cityNameFilterValue: { label: string; value: string };
	searchValue: string;
	searchBarPlaceholder: string;
	pageNumber: number;
	take: number;
	totalAmount: number;
}

function makeInitialState(): ILocationAssignmentState {
	return {
		locations: {
			payload: [],
			inProcess: true,
			error: false,
		},
		blueprint: {
			payload: null,
			inProcess: true,
			error: false,
		},
		saveEnabledLocationsState: {
			inProcess: false,
			error: false,
		},
		addedLocations: [],
		removedLocations: [],
		confirmationModalOpen: false,
		isFilterModalOpen: false,
		locationStates: [],
		locationCityNames: [],
		locationDcNames: [],
		locationDcNumbers: [],
		stateFilterValue: { label: 'Filter By State', value: '' },
		dcNameFilterValue: { label: 'Filter By OpCo Name', value: '' },
		assignmentFilterValue: {
			label: 'Filter By Assignment Status',
			value: null,
		},
		dcNumberFilterValue: { label: 'Filter By OpCo Number', value: '' },
		cityNameFilterValue: { label: 'Filter By City', value: '' },
		searchValue: '',
		searchBarPlaceholder: 'Filter By ID or Name',
		pageNumber: 0,
		take: 100,
		totalAmount: 0,
	};
}

export const slice = Redux.createSlice({
	name: 'locationAssignment',
	initialState: makeInitialState(),
	reducers: {
		locationAssignmentInitialPageLoadedPending() {
			return makeInitialState();
		},
		locationAssignmentPageLoadedPending(state) {
			state.locations.inProcess = true;
			state.locations.error = false;
		},
		locationAssignmentPageLoadedFailure(state) {
			state.locations.inProcess = false;
			state.locations.error = true;
			state.blueprint.inProcess = false;
			state.blueprint.error = true;
		},
		locationAssignmentPageLoadedSuccess(
			state,
			action: Redux.PayloadAction<
				[Hub.LocationAndFilterResponse, Hub.BlueprintStatistics]
			>
		) {
			const [locationRes, blueprint] = action.payload;
			const {
				locations,
				states,
				dcNumbers,
				dcNames,
				cities,
				totalNumLocations,
			} = locationRes;
			state.totalAmount = totalNumLocations;
			const locationsWithInitialValue = locations.map<IEnabledLocation>(
				(x) => ({ ...x, initial: x.enabled })
			);

			state.blueprint = {
				error: false,
				inProcess: false,
				payload: blueprint,
			};
			state.locations = {
				error: false,
				inProcess: false,
				payload: locationsWithInitialValue,
			};

			state.locationStates = states;
			state.locationCityNames = cities;
			state.locationDcNumbers = dcNumbers;
			state.locationDcNames = dcNames;
		},
		appliedFiltersPending(state) {
			state.locations = {
				error: false,
				inProcess: true,
				payload: [],
			};
			state.pageNumber = 0;
		},
		selectedLocationsChanged(state, action: Redux.PayloadAction<number[]>) {
			const selectedLocations = Object.fromEntries(
				action.payload.map((x) => [x, true])
			);

			for (const location of state.locations.payload) {
				location.enabled = selectedLocations[location.id] || false;
			}

			const currentlyAddedLocationIDs = state.addedLocations.map((x) => x.id);
			const currentlyRemovedLocationIDs = state.removedLocations.map(
				(x) => x.id
			);

			state.locations.payload.forEach((x) => {
				const isEnabled = x.enabled;
				const isInitiallyEnabled = x.initial;
				const isChanged = isEnabled != isInitiallyEnabled;

				const isCurrentlyAdded = currentlyAddedLocationIDs.includes(x.id);
				const isCurrentlyRemoved = currentlyRemovedLocationIDs.includes(x.id);

				const addToAddedLocations = isEnabled && isChanged && !isCurrentlyAdded;
				const removeFromAddedLocations = !isEnabled && isCurrentlyAdded;
				const addToRemovedLocations =
					!isEnabled && isChanged && !isCurrentlyRemoved;
				const removeFromRemovedLocations = isEnabled && isCurrentlyRemoved;

				if (addToAddedLocations) {
					state.addedLocations.push(x);
				}

				if (removeFromAddedLocations) {
					const index = state.addedLocations.map((x) => x.id).indexOf(x.id);
					state.addedLocations.splice(index, 1);
				}

				if (addToRemovedLocations) {
					state.removedLocations.push(x);
				}

				if (removeFromRemovedLocations) {
					const index = state.removedLocations.map((x) => x.id).indexOf(x.id);
					state.removedLocations.splice(index, 1);
				}
			});
		},
		saveLocationsClickedSuccess(state) {
			state.confirmationModalOpen = true;
		},
		saveLocationsModalConfirmedPending(state) {
			state.saveEnabledLocationsState = {
				inProcess: true,
				error: false,
			};
			state.confirmationModalOpen = false;
		},
		saveLocationsModalConfirmedFailure(state) {
			state.saveEnabledLocationsState = {
				inProcess: false,
				error: true,
			};
		},
		saveLocationsModalConfirmedSuccess(state) {
			state.saveEnabledLocationsState = { inProcess: false, error: false };
			state.locations.payload = [];
			state.removedLocations = [];
			state.addedLocations = [];
			state.confirmationModalOpen = false;
		},
		saveLocationsModalCancelled(state) {
			state.confirmationModalOpen = false;
		},
		filterModalOpened(state) {
			state.isFilterModalOpen = true;
		},
		filterModalCancelled(state) {
			state.isFilterModalOpen = false;
		},
		stateFilterValueChanged(state, action) {
			if (action.payload === null) {
				state.stateFilterValue = makeInitialState().stateFilterValue;
			} else {
				state.stateFilterValue = action.payload;
			}
		},
		dcNumberFilterValueChanged(state, action) {
			if (action.payload === null) {
				state.dcNumberFilterValue = {
					label: 'Filter By OpCo Number',
					value: '',
				};
			} else {
				state.dcNumberFilterValue = action.payload;
			}
		},
		assignmentFilterValueChanged(state, action) {
			if (action.payload === null) {
				state.assignmentFilterValue = makeInitialState().assignmentFilterValue;
			} else {
				state.assignmentFilterValue = action.payload;
			}
		},
		dcNameFilterValueChanged(state, action) {
			if (action.payload === null) {
				state.dcNameFilterValue = makeInitialState().dcNameFilterValue;
			} else {
				state.dcNameFilterValue = action.payload;
			}
		},
		cityNameFilterValueChanged(state, action) {
			if (action.payload === null) {
				state.cityNameFilterValue = makeInitialState().cityNameFilterValue;
			} else {
				state.cityNameFilterValue = action.payload;
			}
		},
		searchValueChanged(state, action) {
			state.searchValue = action.payload;
			state.pageNumber = 0;
		},
		changePage(state, action) {
			state.pageNumber = action.payload;
		},
	},
});

export const thunks = {
	initialPageLoad(blueprintId: number) {
		return async (dispatch) => {
			dispatch(slice.actions.locationAssignmentInitialPageLoadedPending());
			dispatch(thunks.pageLoad(blueprintId));
		};
	},
	pageLoad(blueprintId: number) {
		return async (dispatch, getState: () => IAppState) => {
			dispatch(slice.actions.locationAssignmentPageLoadedPending());
			const state = getState().locationAssignment;

			const [locationRes, blueprintRes] = await Promise.all([
				await Hub.LocationControllerV3.GetLocationsForBlueprint(
					blueprintId,
					state.stateFilterValue.value,
					state.cityNameFilterValue.value,
					state.dcNameFilterValue.value,
					state.dcNumberFilterValue.value,
					state.searchValue,
					state.assignmentFilterValue.value,
					state.pageNumber * state.take,
					state.take
				),
				await Hub.BlueprintController.GetById(blueprintId),
			]);

			if (!locationRes.ok || !blueprintRes.ok) {
				dispatch(slice.actions.locationAssignmentPageLoadedFailure());
				return;
			}

			dispatch(
				slice.actions.locationAssignmentPageLoadedSuccess([
					locationRes.value,
					blueprintRes.value,
				])
			);
		};
	},
	refetchPage() {
		return async (dispatch, getState: () => IAppState) => {
			const state = getState().locationAssignment;
			const blueprintId = state.blueprint.payload.id;
			dispatch(thunks.pageLoad(blueprintId));
		};
	},
	saveLocationsClicked() {
		return async (dispatch, getState: () => IAppState) => {
			const state = getState();
			const hasLocationsToSave =
				state.locationAssignment.addedLocations.length > 0 ||
				state.locationAssignment.removedLocations.length > 0;

			if (!hasLocationsToSave) {
				dispatch(push('/'));
				return;
			}

			dispatch(slice.actions.saveLocationsClickedSuccess());
		};
	},
	saveLocationsModalConfirmed(args: {
		blueprintId: number;
		successMessage: IToastMessage;
	}) {
		return async (dispatch, getState: () => IAppState) => {
			const { blueprintId, successMessage } = args;
			const state = getState();

			dispatch(slice.actions.saveLocationsModalConfirmedPending());
			const changedLocationIds: Hub.ChangedLocations = {
				removedLocationIds: state.locationAssignment.removedLocations.map(
					(x) => x.id
				),
				addedLocationIds: state.locationAssignment.addedLocations.map(
					(x) => x.id
				),
			};

			const res = await Hub.BlueprintControllerV3.SetActiveLocationsV3(
				blueprintId,
				changedLocationIds
			);

			if (!res.ok) {
				dispatch(slice.actions.saveLocationsModalConfirmedFailure());
				toastr.error({ body: 'Could not save locations.' });
				return;
			}

			dispatch(slice.actions.saveLocationsModalConfirmedSuccess());
			toastr.success(successMessage);
			dispatch(push('/'));
		};
	},
	filterChanged() {
		return async (dispatch) => {
			dispatch(slice.actions.appliedFiltersPending());
			dispatch(thunks.refetchPage());
		};
	},
	stateFilterChanged(action) {
		return async (dispatch) => {
			dispatch(slice.actions.stateFilterValueChanged(action));
			dispatch(thunks.filterChanged());
		};
	},
	cityNameFilterChanged(action) {
		return async (dispatch) => {
			dispatch(slice.actions.cityNameFilterValueChanged(action));
			dispatch(thunks.filterChanged());
		};
	},
	dcNumberFilterChanged(action) {
		return async (dispatch) => {
			dispatch(slice.actions.dcNumberFilterValueChanged(action));
			dispatch(thunks.filterChanged());
		};
	},
	dcNameFilterChanged(action) {
		return async (dispatch) => {
			dispatch(slice.actions.dcNameFilterValueChanged(action));
			dispatch(thunks.filterChanged());
		};
	},
	assignmentFilterChanged(action) {
		return async (dispatch) => {
			dispatch(slice.actions.assignmentFilterValueChanged(action));
			dispatch(thunks.filterChanged());
		};
	},
	searchBarChanged(action) {
		return async (dispatch) => {
			dispatch(slice.actions.searchValueChanged(action));
			dispatch(thunks.refetchPage());
		};
	},
	onPageChange(action) {
		return async (dispatch) => {
			dispatch(slice.actions.changePage(action));
			dispatch(thunks.refetchPage());
		};
	},
};
